/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable no-console */

import React, { FC, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { History } from "history"
import { actionCreator } from "store/store"
import { AxiosAction } from "types/api"
import { UserProfileInfo } from "types/models/user_profile.type"
import { storageHelper } from "utils/storage"
import { ECOMMERCE_URL_PREFIX, MENUS } from "constants/routes"
import { Spin } from "@duxton-web/ui"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useLazyGetUserProfileDetailQuery } from "queries/authApi"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import { hasEcommerceStore } from "utils/user_helper"
import { setupMexPortalFeatureFlag } from "./featureFlag"

const applyUserProfileAndRedirect = async (
  userProfileInfo: UserProfileInfo,
  history: History,
) => {
  storageHelper.setUserProfileInfo(userProfileInfo)

  await setupMexPortalFeatureFlag()

  const hasAccessOrderManager = storageHelper.getMexPortalFeatureFlags(
    "hasAccessOrderManager",
  )

  const businessLineIds = storageHelper.getBusinessLineIds()

  /**
   * simplify logic in mex-portal
   * directly add GF to businessLineIds as this is one of the business line to show order menu
   * ref:
   * - https://gitlab.myteksi.net/grab-transport/mex/mex-portal/-/blob/master/src/utils/LoginHelper.js#L810
   * - https://gitlab.myteksi.net/gfg/troy/troy-portal/-/blob/master/utils/index.js#L417
   */
  if (hasAccessOrderManager) {
    businessLineIds.push("GF")
    storageHelper.setBusinessLineIds(businessLineIds)
  }

  history.replace(hasEcommerceStore() ? ECOMMERCE_URL_PREFIX : MENUS)
}

export const LoginSuccess: FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(false)

  const {
    flags: { getUserProfileDetail: isRtkGetUserProfileDetailEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [getUserProfileDetailRtk] = useLazyGetUserProfileDetailQuery()

  useEffect(() => {
    if (isRtkFlagLoading) return

    const fetchUserProfile = async () => {
      setIsLoading(true)
      try {
        let userProfileInfo: UserProfileInfo | undefined

        // While migration flags are loading, do not fall back to old Redux: the enabled bit is
        // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
        // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
        // the trigger UI while loading so clicks are not silently dropped.
        if (isRtkGetUserProfileDetailEnabled) {
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.getUserProfileDetail,
            labels: {
              IS_RTK_ENABLED: isRtkGetUserProfileDetailEnabled,
              FROM_RTK_QUERY: true,
              FROM_OLD_REDUX: false,
              ACTION_NAME: "getUserProfileDetail",
            },
          })

          const result = await getUserProfileDetailRtk()

          if (result.error) {
            throw new Error("Failed to fetch user profile")
          }

          userProfileInfo = result.data
        } else {
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.getUserProfileDetail,
            labels: {
              IS_RTK_ENABLED: isRtkGetUserProfileDetailEnabled,
              FROM_RTK_QUERY: false,
              FROM_OLD_REDUX: true,
              ACTION_NAME: "getUserProfileDetail",
            },
          })

          const response = ((await dispatch(
            actionCreator.auth.getUserProfileDetail(),
          )) as unknown) as AxiosAction<UserProfileInfo>

          userProfileInfo = response?.payload?.data
        }

        if (!userProfileInfo) {
          throw new Error("Failed to fetch user profile")
        }

        await applyUserProfileAndRedirect(userProfileInfo, history)
      } catch (err) {
        console.error("Error fetching user profile:", err)
        history.replace("/login")
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserProfile()
  }, [
    dispatch,
    getUserProfileDetailRtk,
    history,
    isRtkFlagLoading,
    isRtkGetUserProfileDetailEnabled,
  ])

  return (
    <div
      style={{
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        flexDirection: "column",
      }}
    >
      <h1 style={{ marginBottom: "80px" }}>Setup User Data (for dev only)</h1>
      {isLoading && <Spin size="large" />}
    </div>
  )
}
